import React from 'react'
import { Table, Th, Td, Tr } from '@/shared/ui/Table'
import { Button, Input, Badge, Checkbox } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreVertical, Search } from 'lucide-react'
import { Pagination } from '@/shared/ui/Pagination'
import type { Customer, CustomerStatus } from '../types'

type Props = {
  items: Customer[]
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  search: string
  onSearchChange: (v: string) => void
  status: CustomerStatus | ''
  onStatusChange: (v: CustomerStatus | '') => void
  planId?: string | ''
  onPlanChange?: (v: string | '') => void
  plans?: Array<{ id: string; name: string }>
  onCreate: () => void
  onEdit: (c: Customer) => void
  onChangePassword?: (c: Customer) => void
  onDelete: (c: Customer) => void
  onToggleActive: (c: Customer) => void
  canBulkSelect?: boolean
  selectedIds: Set<string>
  allOnPageSelected: boolean
  onToggleSelect: (id: string, checked: boolean) => void
  onToggleSelectPage: (checked: boolean) => void
  onSync?: (c: Customer) => void
}

export default function CustomerTable({ items, total, page, pageSize, onPageChange, search, onSearchChange, status, onStatusChange, planId = '', onPlanChange, plans = [], onCreate, onEdit, onChangePassword, onDelete, onToggleActive, canBulkSelect = false, selectedIds, allOnPageSelected, onToggleSelect, onToggleSelectPage, onSync }: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const [q, setQ] = React.useState(search)
  const [qStatus, setQStatus] = React.useState<CustomerStatus | ''>(status)
  const [qPlan, setQPlan] = React.useState<string | ''>(planId)
  React.useEffect(() => setQ(search), [search])
  React.useEffect(() => setQStatus(status), [status])
  React.useEffect(() => setQPlan(planId), [planId])
  React.useEffect(() => {
    const id = setTimeout(() => {
      if (q !== search) onSearchChange(q)
    }, 400)
    return () => clearTimeout(id)
  }, [q])
  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex w-full max-w-xl items-center gap-2">
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar clientes" className="pl-9" />
          </div>
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={qStatus}
            onChange={(e)=> setQStatus(e.target.value as CustomerStatus | '')}
          >
            <option value="">Todos</option>
            <option value="ativo">Ativo</option>
            <option value="pendente">Pendente</option>
            <option value="suspenso">Suspenso</option>
            <option value="inativo">Inativo</option>
          </select>
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={qPlan}
            onChange={(e)=> setQPlan(e.target.value)}
          >
            <option value="">Todos os planos</option>
            {plans.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <Button onClick={() => { onStatusChange(qStatus); onPlanChange?.(qPlan) }}>Filtrar</Button>
        </div>
        <Button onClick={onCreate}>Novo cliente</Button>
      </div>

      <Table>
        <thead>
          <Tr>
            {canBulkSelect && (
              <Th className="w-8">
                <Checkbox
                  aria-label="Selecionar todos nesta página"
                  checked={allOnPageSelected}
                  onChange={(e)=> onToggleSelectPage((e.target as HTMLInputElement).checked)}
                />
              </Th>
            )}
            <Th>Nome</Th>
            <Th>Email</Th>
            <Th>Plano atual</Th>
            <Th>Data cadastro</Th>
            <Th>Status</Th>
            <Th className="w-12 text-right">Ações</Th>
          </Tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <Tr key={c.id}>
              {canBulkSelect && (
                <Td>
                  <Checkbox
                    aria-label={`Selecionar ${c.name || c.email || c.id}`}
                    checked={selectedIds.has(c.id)}
                    onChange={(e)=> onToggleSelect(c.id, (e.target as HTMLInputElement).checked)}
                  />
                </Td>
              )}
              <Td>{c.name ?? '—'}</Td>
              <Td>{c.email ?? '—'}</Td>
              <Td>{c.current_plan_name || '—'}</Td>
              <Td>{c.created_at ? new Date(c.created_at).toISOString().replace('T',' ').slice(0,19) + ' UTC' : '—'}</Td>
              <Td>
                <Badge
                  variant="soft"
                  color={
                    c.status === 'ativo' ? 'success' :
                    c.status === 'pendente' ? 'info' :
                    c.status === 'suspenso' ? 'warning' :
                    'error'
                  }
                  className="capitalize"
                >
                  {c.status}
                </Badge>
              </Td>
              <Td className="text-right">
                <DropdownMenu
                  align="end"
                  trigger={<Button variant="outline" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button>}
                  items={[
                    { label: c.status === 'ativo' ? 'Inativar' : 'Ativar', onSelect: () => onToggleActive(c) },
                    { label: 'Editar', onSelect: () => onEdit(c) },
                    ...(onChangePassword ? [{ label: 'Alterar senha', onSelect: () => onChangePassword(c) }] : []),
                    ...(onSync ? [{ label: 'Sincronizar assinatura', onSelect: () => onSync(c) }] : []),
                    { label: <span className="text-destructive">Excluir</span>, onSelect: () => onDelete(c) },
                  ]}
                />
              </Td>
            </Tr>
          ))}
          {items.length === 0 && (
            <Tr>
              <Td colSpan={canBulkSelect ? 7 : 6} className="py-10 text-center text-sm text-foreground/70">Nenhum cliente encontrado.</Td>
            </Tr>
          )}
        </tbody>
      </Table>

      <div className="mt-4 flex justify-end">
        <Pagination page={page} total={totalPages} onChange={onPageChange} />
      </div>
    </div>
  )
}
